import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Gauge, Percent, Scale, TrendingUp } from 'lucide-react'
import { getBalanceSheetGaap, getIncomeStatementGaap } from '../../api/accounting'
import { Card, Tile } from '../../components/Summary'
import { HelpTip } from '../../components/HelpTip'
import { localToday } from '../../utils/format'
import { money } from '../sales/salesShared'
import { monthName, thisYm } from './accountingShared'
import { ReportsTabs, periodDates } from './reportsShared'
import { useSession } from '../../session'

const GROUPS = [
  { title: 'Financial statements', items: [
    { to: '/accounting/reports/income-statement', label: 'Income statement', k: 'incomeStatement' },
    { to: '/accounting/reports/balance-sheet', label: 'Balance sheet', k: 'balanceSheet' },
    { to: '/accounting/reports/trial-balance', label: 'Trial balance', k: 'trialBalance' },
    { to: `/accounting/reports/period-package?period=${thisYm()}`, label: 'Month-end report pack (print)', help: 'The month’s income statement and balance sheet together, ready to print or save as PDF.' },
  ] },
  { title: 'Ledgers', items: [
    { to: '/accounting/reports/general-ledger', label: 'General ledger', k: 'generalLedger' },
    { to: '/accounting/reports/ap-subledger', label: 'Payables subledger', k: 'apSubledger' },
    { to: '/accounting/reports/ar-subledger', label: 'Receivables subledger', k: 'arSubledger' },
    { to: '/accounting/reports/jobs-in-progress', label: 'Jobs in progress', help: 'Long jobs: how far along each is, what it has earned against what’s been billed, and the month-end adjustment.', module: 'jobs' },
    { to: '/inventory/production/cogm', label: 'Cost of goods manufactured', help: 'What you made in a period cost: materials, labour and overhead, the change in work in progress, and finished goods.', module: 'production' },
  ] },
  { title: 'Setup', items: [
    { to: '/accounting/reports/classification', label: 'Account classification', k: 'classification' },
  ] },
]

// Reports: the key figures, then every report.
export default function ReportsMenu() {
  const { has } = useSession()
  const [month, setMonth] = useState(null)
  const [year, setYear] = useState(null)
  const [bs, setBs] = useState(null)

  useEffect(() => {
    const [ms, me] = periodDates('this_month')
    const [ys, ye] = periodDates('ytd')
    getIncomeStatementGaap({ start: ms, end: me, compare: 'previous' }).then(setMonth).catch(() => setMonth(false))
    getIncomeStatementGaap({ start: ys, end: ye, compare: 'none' }).then(setYear).catch(() => setYear(false))
    getBalanceSheetGaap({ as_of: localToday(), compare: 'none' }).then(setBs).catch(() => setBs(false))
  }, [])

  const m = month?.figures
  const pm = month?.prior_figures
  const sec = (key) => [...(bs?.assets?.sections || []), ...(bs?.liabilities?.sections || [])].find((s) => s.key === key)?.total || 0
  const ca = sec('current_asset')
  const cl = sec('current_liability')
  const label = monthName(thisYm(), { year: false })

  return (
    <div className="section-page">
      <ReportsTabs />
      <header className="section-header"><h1>Reports</h1></header>

      <div className="sum-tiles">
        <Tile label={`Net income, ${label}`} href="/accounting/reports/income-statement" icon={TrendingUp}
          value={m ? money(m.net_income) : '…'}
          note={m && pm ? `${money(pm.net_income)} last month` : ''}
          tone={m && m.net_income < 0 ? 'warn' : undefined} />
        <Tile label="Gross margin this year" href="/accounting/reports/income-statement?period=ytd" icon={Percent}
          value={year?.figures ? (year.figures.gross_margin_pct != null ? `${year.figures.gross_margin_pct}%` : '–') : '…'}
          note={year?.figures ? `${money(year.figures.gross_profit)} gross profit` : ''} />
        <Tile label="Current ratio" href="/accounting/reports/balance-sheet" icon={Gauge}
          value={bs ? (cl > 0 ? (ca / cl).toFixed(2) : '–') : '…'}
          note={bs ? `${money(ca)} current assets · ${money(cl)} current liabilities` : ''}
          tone={bs && cl > 0 && ca / cl < 1 ? 'warn' : undefined} />
        <Tile label="Working capital" href="/accounting/reports/balance-sheet" icon={Scale}
          value={bs ? money(ca - cl) : '…'}
          note={bs ? (bs.balanced ? 'Balance sheet balances' : 'Balance sheet is out of balance') : ''}
          tone={bs && !bs.balanced ? 'warn' : undefined} />
      </div>
      <p className="sum-muted fs-ratio-help">
        Current ratio <HelpTip k="currentRatio" /> · Working capital <HelpTip k="workingCapital" />
      </p>

      <div className="fs-groups">
        {GROUPS.map((g) => (
          <Card key={g.title} title={g.title}>
            <ul className="sum-list acc-reports">
              {g.items.filter((r) => !r.module || has(r.module)).map((r) => (
                <li key={r.to}><span className="sum-list-main"><Link to={r.to}>{r.label}</Link><HelpTip k={r.k} text={r.help} /></span></li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </div>
  )
}
